import { useReadPaymentRecords } from '@/api/accounting-dealer'
import {
  Alert,
  AlertTitle,
  Box,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { ErrorOutline } from '@mui/icons-material'
import { ServiceCard } from './service-card'
import { useCompany } from '@/context/companies'
import { useMemo } from 'react'
import { AdditionalService } from '@/types'

export function WaitingServicesList() {
  const { company } = useCompany()

  const { data, error, isLoading } = useReadPaymentRecords({
    status: 'waiting',
    deal_key: company.deal.key,
  })

  const { data: records = [] } = data ?? {}

  // Group records by payment_plan_id and show only one record per payment plan
  const uniqueRecords = useMemo(() => {
    // Use reduce to process all records in a single pass
    const recordMap = records.reduce((result, currentRecord) => {
      const { payment_plan_id, id, invoice } = currentRecord

      const record = {
        ...currentRecord,
        cashflow_date: `${invoice.year}-${invoice.month}-01`,
      }

      // Handle records with payment_plan_id
      if (payment_plan_id) {
        // Check if we already have a record with this payment_plan_id
        const existingRecord = result.get(payment_plan_id)

        // Find the first installment record (with pattern "(1/X)")
        const firstInstallment = records.find(
          (record) =>
            record.payment_plan_id === payment_plan_id &&
            record.description.includes('(1/'),
        )!.invoice

        if (existingRecord) {
          // Update the existing record's amount
          result.set(payment_plan_id, {
            ...existingRecord,
            // clean description by removing installment patterns like "(1/2)"
            description: `${existingRecord.description.replace(/\s*\(\d+\/\d+\)\s*/, '').trim()}`,
            cashflow_date: `${firstInstallment.year}-${firstInstallment.month}-01`,
          })
        } else {
          // This is the first record with this payment_plan_id
          result.set(payment_plan_id, record)
        }
      } else {
        // Records without payment_plan_id are always included
        result.set(id, record)
      }

      return result
    }, new Map<number, AdditionalService>())

    return Array.from(recordMap.values())
  }, [records])

  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[...Array(2)].map((_, index) => (
          <Skeleton key={index} variant="rounded" height={100} />
        ))}
      </Stack>
    )
  }

  if (error) {
    return (
      <Box textAlign="center">
        <ErrorOutline color="error" fontSize="large" />
        <Typography color="gray">
          Não foi possível carregar os serviços pendentes.
        </Typography>
      </Box>
    )
  }

  if (uniqueRecords.length === 0) {
    return null
  }

  const hasPendingServices = uniqueRecords.length > 0

  return (
    <Stack spacing={2}>
      {hasPendingServices && (
        <Alert icon={false} severity="warning">
          <AlertTitle>Serviços pendentes</AlertTitle>
          Os serviços pendentes devem ser revisados ...
        </Alert>
      )}
      {uniqueRecords.map((record) => (
        <ServiceCard key={record.id} data={record} />
      ))}
    </Stack>
  )
}
